import React, { useEffect, useState } from 'react';

import {
    Box,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
    Avatar,
    Divider,
    Tooltip,
    IconButton,
    CircularProgress,
    Collapse,
} from '@mui/material';
import {
    Dashboard as DashboardIcon,
    Assignment as AssignmentIcon,
    People as PeopleIcon,
    Logout as LogoutIcon,
    AdminPanelSettings as AdminIcon,
    Description as RequestIcon,
    Insights as InsightsIcon,
    DynamicForm as FormsIcon,
    Settings as SettingsIcon,
    Person as PersonIcon,
    Business as BusinessIcon,
    Assessment as AssessmentIcon,
    Groups as GroupsIcon,
    Feedback as FeedbackIcon,
    Folder as FolderIcon,

    RocketLaunch as RocketLaunchIcon,
    TableChart as TableChartIcon,
    Psychology as PsychologyIcon,
    ExpandLess as ExpandLessIcon,
    ExpandMore as ExpandMoreIcon,
    AutoStories as AutoStoriesIcon,
    Close as CloseIcon,
    Task as TaskIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useUserPages, UserPage } from '../hooks/useUserPages';
import { SEARCE_COLORS } from '../theme';

const searceLogo = '/Logo_blue.jpg';

export const DRAWER_WIDTH_EXPANDED = 280;
export const DRAWER_WIDTH_COLLAPSED = 64;

/** Insights pages nested under the Key Insights dropdown (display order). */
const KEY_INSIGHTS_ORDER = [
    'dashboard',      // Utilization
    'projects',  
    'my-insights',
    'my-team',        // Team Insights

] as const;

const PAGE_ORDER = [
    'dashboard',
    'projects',
    'ai-chatbot',
    'my-insights',
    'my-team',
    '/customer-feedback-analytics',
    'internal-project-request',
    'searce-assets',
    '/dri-insights',
    'squad-structure',
    'requests',
    'important-links',
    'user-feedback',
    'admin',
    'projects-portfolio',
    'solution-story',
    'zoho-tasks',
];

const toPageSlug = (name: string) =>
    name.toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-').replace(/^\//, '');

const isKeyInsightsPage = (name: string) =>
    (KEY_INSIGHTS_ORDER as readonly string[]).includes(toPageSlug(name));

interface DynamicSidebarProps {
    open: boolean;
    onToggle: () => void;
    activeTab: string;
    onTabChange: (tab: string) => void;
    /** Renders as a temporary pull-out overlay instead of the permanent hover drawer. */
    isMobile?: boolean;
    mobileOpen?: boolean;
    onMobileClose?: () => void;
}

const getPageIcon = (pageName: string): React.ReactElement => {
    const iconMap: { [key: string]: React.ReactElement } = {
        'dashboard': <DashboardIcon />,
        'projects': <AssignmentIcon />,
        'projects-portfolio': <TableChartIcon />,
        'projects_portfolio': <TableChartIcon />,
        'ai-chatbot': <PsychologyIcon />,
        'ai_chatbot': <PsychologyIcon />,
        'people-analytics': <PeopleIcon />,
        'people_analytics': <PeopleIcon />,
        'my-insights': <InsightsIcon />,
        'my-team': <GroupsIcon />,
        'dri-insights': <InsightsIcon />,
        'dri_insights': <InsightsIcon />,
        'user-feedback': <FeedbackIcon />,
        'user_feedback': <FeedbackIcon />,
        'customer-sentiment': <AssessmentIcon />,
        'customer_sentiment': <AssessmentIcon />,
        'customer-feedback-analytics': <AssessmentIcon />,
        'important-links': <FolderIcon />,
        'important_links': <FolderIcon />,
        'my-files': <FolderIcon />,
        'my_files': <FolderIcon />,


        // Assets
        'searce-assets': <RocketLaunchIcon />,
        'searce_assets': <RocketLaunchIcon />,
        'squad-structure': <BusinessIcon />,
        'squad_structure': <BusinessIcon />,
        'requests': <RequestIcon />,
        'internal-project-request': <RequestIcon />,
        'admin': <AdminIcon />,
        'administration': <AdminIcon />,
        'forms': <FormsIcon />,
        'settings': <SettingsIcon />,
        'profile': <PersonIcon />,
        'solution-story': <AutoStoriesIcon />,
        'solution_story': <AutoStoriesIcon />,
        'solution-stories': <AutoStoriesIcon />,
        'zoho-tasks': <TaskIcon />,
        'zoho_tasks': <TaskIcon />,
    };

    return iconMap[pageName] || iconMap[pageName.replace(/_/g, '-')] || <DashboardIcon />;
};

const navItemButtonSx = (isActive: boolean, open: boolean, nested = false) => ({
    minHeight: nested ? 40 : 48,
    px: open ? 2 : 1,
    pl: open && nested ? 4 : undefined,
    borderRadius: '8px',
    mx: open ? 1 : 0.5,
    backgroundColor: isActive ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
    '&:hover': {
        backgroundColor: isActive ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)',
    },
    '&.Mui-selected': {
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
        },
    },
    display: 'flex',
    justifyContent: open ? 'flex-start' : 'center',
    alignItems: 'center',
});

const DynamicSidebar: React.FC<DynamicSidebarProps> = ({
    open,
    onToggle,
    activeTab,
    onTabChange,
    isMobile = false,
    mobileOpen = false,
    onMobileClose,
}) => {
    const { user, logout } = useAuth();
    const { pages, loading, error } = useUserPages();
    const [keyInsightsOpen, setKeyInsightsOpen] = useState(false);

    // On mobile the drawer is a full pull-out overlay, so it's always shown expanded while open.
    const expanded = isMobile ? true : open;

    const handleItemClick = (itemId: string) => {
        onTabChange(itemId);
        if (isMobile) {
            onMobileClose?.();
        }
    };

    const handleLogout = async () => {
        try {
            await logout();
        } catch (err) {
            console.error('Logout error:', err);
        }
    };

    const handleMouseEnter = () => {
        if (!open) {
            onToggle();
        }
    };

    const handleMouseLeave = () => {
        if (open) {
            onToggle();
        }
    };

    const getUserInitials = (name: string) => {
        return name
            .split(' ')
            .map(n => n[0])
            .join('')
            .toUpperCase()
            .substring(0, 2);
    };

    const normalizePageName = (name: string) =>
        name.toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-');

    const sortedPages = [...pages].sort((a, b) => {
        const indexA = PAGE_ORDER.indexOf(a.name);
        const indexB = PAGE_ORDER.indexOf(b.name);

        if (indexA !== -1 && indexB !== -1) {
            return indexA - indexB;
        }
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        return a.sort_order - b.sort_order;
    });

    const visiblePages = sortedPages.filter(
        (page) => normalizePageName(page.name) !== 'executive-summary'
    );

    const keyInsightsPages = KEY_INSIGHTS_ORDER
        .map((slug) => visiblePages.find((page) => toPageSlug(page.name) === slug))
        .filter((page): page is UserPage => Boolean(page));

    const keyInsightsActive = isKeyInsightsPage(activeTab);

    useEffect(() => {
        if (keyInsightsActive) {
            setKeyInsightsOpen(true);
        }
    }, [keyInsightsActive]);

    type NavEntry =
        | { kind: 'key-insights' }
        | { kind: 'page'; page: UserPage };

    const navEntries: NavEntry[] = [];
    let keyInsightsInserted = false;
    for (const page of visiblePages) {
        if (isKeyInsightsPage(page.name)) {
            if (!keyInsightsInserted && keyInsightsPages.length > 0) {
                navEntries.push({ kind: 'key-insights' });
                keyInsightsInserted = true;
            }
            continue;
        }
        navEntries.push({ kind: 'page', page });
    }

    const renderPageItem = (page: UserPage, nested = false) => {
        const isActive = activeTab === page.name;
        return (
            <ListItem key={page.id} disablePadding sx={{ mb: nested ? 0.5 : 1 }}>
                <ListItemButton
                    onClick={() => handleItemClick(page.name)}
                    sx={navItemButtonSx(isActive, expanded, nested)}
                >
                    <ListItemIcon
                        sx={{
                            minWidth: 0,
                            mr: expanded ? 2 : 0,
                            color: SEARCE_COLORS.white,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {getPageIcon(toPageSlug(page.name))}
                    </ListItemIcon>
                    {expanded && (
                        <ListItemText
                            primary={page.sidebar_name}
                            sx={{
                                '& .MuiListItemText-primary': {
                                    color: SEARCE_COLORS.white,
                                    fontWeight: isActive ? 600 : 400,
                                    fontSize: nested ? '0.85rem' : '0.9rem',
                                    whiteSpace: 'nowrap',
                                    lineHeight: 1.2,
                                },
                            }}
                        />
                    )}
                    {!expanded && (
                        <Tooltip title={page.sidebar_name} placement="right">
                            <Box />
                        </Tooltip>
                    )}
                </ListItemButton>
            </ListItem>
        );
    };

    const paperWidth = expanded ? DRAWER_WIDTH_EXPANDED : DRAWER_WIDTH_COLLAPSED;

    return (
        <Drawer
            variant={isMobile ? 'temporary' : 'permanent'}
            open={isMobile ? mobileOpen : true}
            onClose={isMobile ? onMobileClose : undefined}
            onMouseEnter={!isMobile ? handleMouseEnter : undefined}
            onMouseLeave={!isMobile ? handleMouseLeave : undefined}
            ModalProps={isMobile ? { keepMounted: true } : undefined}
            sx={{
                width: isMobile ? 0 : DRAWER_WIDTH_COLLAPSED,
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                    width: paperWidth,
                    boxSizing: 'border-box',
                    backgroundColor: SEARCE_COLORS.sidebarBg,
                    color: SEARCE_COLORS.white,
                    border: 'none',
                    borderRight: `1px solid ${SEARCE_COLORS.primaryDark}`,
                    transition: 'width 0.3s ease-in-out',
                    overflowX: 'hidden',
                    overflowY: 'auto',
                    position: 'fixed',
                    zIndex: 1300,
                    height: '100vh',
                    boxShadow: '2px 0 8px rgba(0, 100, 255, 0.15)',
                },
            }}
        >
            <Box sx={{ p: 2, minHeight: 64 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: expanded ? 'space-between' : 'center',
                        mb: 3,
                        minHeight: 40,
                    }}
                >
                    <Box
                        component="img"
                        src={searceLogo}
                        alt="Searce Logo"
                        sx={{
                            height: expanded ? 40 : 32,
                            width: 'auto',
                            maxWidth: expanded ? 140 : 32,
                            objectFit: 'contain',
                        }}
                    />
                    {isMobile && (
                        <IconButton
                            onClick={onMobileClose}
                            size="small"
                            aria-label="Close navigation"
                            sx={{
                                color: 'rgba(255, 255, 255, 0.7)',
                                '&:hover': {
                                    color: SEARCE_COLORS.white,
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                },
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    )}
                </Box>

                {user && (
                    <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, minHeight: 40, position: 'relative' }}>
                            <Avatar
                                src={user.picture}
                                onDoubleClick={!expanded ? handleLogout : undefined}
                                sx={{
                                    width: 40,
                                    height: 40,
                                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                    color: SEARCE_COLORS.white,
                                    mr: expanded ? 2 : 0,
                                    flexShrink: 0,
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    cursor: !expanded ? 'pointer' : 'default',
                                    '&:hover': !expanded ? {
                                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                    } : {},
                                }}
                            >
                                {!user.picture && getUserInitials(user.name)}
                            </Avatar>
                            {expanded && (
                                <Box sx={{ flex: 1 }}>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            color: SEARCE_COLORS.white,
                                            fontWeight: 600,
                                            fontSize: '0.9rem',
                                            whiteSpace: 'nowrap',
                                            lineHeight: 1.2,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            maxWidth: '140px',
                                        }}
                                    >
                                        {user.name}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: 'rgba(255, 255, 255, 0.7)',
                                            fontSize: '0.75rem',
                                            lineHeight: 1.2,
                                            textTransform: 'capitalize',
                                            wordBreak: 'break-word',
                                        }}
                                    >
                                        {user.role?.display_name || user.role?.name || 'User'}
                                    </Typography>
                                </Box>
                            )}
                            {expanded && (
                                <IconButton
                                    onClick={handleLogout}
                                    size="small"
                                    sx={{
                                        color: 'rgba(255, 255, 255, 0.7)',
                                        '&:hover': {
                                            color: SEARCE_COLORS.white,
                                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                        },
                                        ml: 1,
                                    }}
                                    title="Logout"
                                >
                                    <LogoutIcon fontSize="small" />
                                </IconButton>
                            )}
                            {!expanded && (
                                <Tooltip title={`${user.name} - Double-click avatar to logout`} placement="right">
                                    <Box />
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                )}

                <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.2)', mb: 2 }} />

                <List sx={{ p: 0 }} role={loading ? 'status' : undefined} aria-busy={loading || undefined}>
                    {loading ? (
                        <ListItem disablePadding sx={{ mb: 1, justifyContent: 'center', display: 'flex' }}>
                            <CircularProgress size={24} sx={{ color: SEARCE_COLORS.white }} />
                        </ListItem>
                    ) : error ? (
                        <ListItem disablePadding sx={{ mb: 1 }}>
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center' }}>
                                Error loading pages
                            </Typography>
                        </ListItem>
                    ) : (
                        navEntries.map((entry) => {
                            if (entry.kind === 'page') {
                                return renderPageItem(entry.page);
                            }

                            return (
                                <Box key="key-insights">
                                    <ListItem disablePadding sx={{ mb: 1 }}>
                                        <ListItemButton
                                            onClick={() => setKeyInsightsOpen((prev) => !prev)}
                                            sx={navItemButtonSx(keyInsightsActive && (!keyInsightsOpen || !expanded), expanded)}
                                        >
                                            <ListItemIcon
                                                sx={{
                                                    minWidth: 0,
                                                    mr: expanded ? 2 : 0,
                                                    color: SEARCE_COLORS.white,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <InsightsIcon />
                                            </ListItemIcon>
                                            {expanded && (
                                                <>
                                                    <ListItemText
                                                        primary="Key Insights"
                                                        sx={{
                                                            '& .MuiListItemText-primary': {
                                                                color: SEARCE_COLORS.white,
                                                                fontWeight: keyInsightsActive ? 600 : 400,
                                                                fontSize: '0.9rem',
                                                                whiteSpace: 'nowrap',
                                                                lineHeight: 1.2,
                                                            },
                                                        }}
                                                    />
                                                    {keyInsightsOpen ? (
                                                        <ExpandLessIcon sx={{ color: SEARCE_COLORS.white, fontSize: 20 }} />
                                                    ) : (
                                                        <ExpandMoreIcon sx={{ color: SEARCE_COLORS.white, fontSize: 20 }} />
                                                    )}
                                                </>
                                            )}
                                            {!expanded && (
                                                <Tooltip title="Key Insights" placement="right">
                                                    <Box />
                                                </Tooltip>
                                            )}
                                        </ListItemButton>
                                    </ListItem>
                                    <Collapse in={keyInsightsOpen && expanded} timeout="auto" unmountOnExit>
                                        <List component="div" disablePadding sx={{ mb: 1 }}>
                                            {keyInsightsPages.map((page) => renderPageItem(page, true))}
                                        </List>
                                    </Collapse>
                                </Box>
                            );
                        })
                    )}
                </List>
            </Box>
        </Drawer>
    );
};

export default DynamicSidebar;
